import React, { useState } from "react";
import { Anchor, Box, FileInput, Form, FormField } from "grommet";
import { objectToFormData } from "../utils/files";
import { handleErrors } from "../utils/errors";
import Papa from "papaparse";
import FontAwesomeIcon from "./FontAwesomeIcon";
import {
    IconFile,
    IconDownload
} from '@tabler/icons-react';
import StatusMessage from "./StatusMessage";
import { sampleData, sampleHeaders } from "../constants/CustomerCSVFields";
import { DisplayInlineBlockBox } from "../utils/styled";
import Button from "./Button";

const UploadCSVForm = ({ onClose }) => {
    const [csvFile, setCsvFile] = useState();
    const [headers, setHeaders] = useState([]);
    const [error, setError] = useState();

    const parseHeaders = (file) => {
        if (file && file[0]) {
            Papa.parse(file[0], {
                header: true,
                complete: (results) => {
                    const headers = results.meta.fields;
                    const filteredHeaders = headers.filter((header) => sampleHeaders.includes(header));
                    if (filteredHeaders.length >= 3) {
                        setError();
                    } else {
                        setError(true);
                    }
                    setHeaders(filteredHeaders);
                },
            });
        } else {
            setHeaders([]);
        }
    };

    const handleCSVUpload = () => {
        if (csvFile) {
            let body = objectToFormData(csvFile);
            if (body) {
                fetch("/api/customers/import_from_csv", {
                    method: "POST",
                    body: body,
                })
                    .catch(handleErrors)
                    .then((data) => {
                        if (!data) {
                            return;
                        }
                        onClose();
                    });
            }
        }
    };

    const handleCSVUploadChange = (values) => {
        if (values?.csv.length > 0) {
            setCsvFile(values);
            parseHeaders(values?.csv);
        } else {
            setHeaders([]);
            setError();
            setCsvFile();
        }
    };

    // sample csv file generation
    const loadSampleCSV = () => {
        if (typeof window !== "undefined") {
            let csv = sampleHeaders.join(",") + "\r\n";
            sampleData.forEach((row) => {
                csv += row.join(",");
                csv += "\r\n";
            });
            let hiddenElement = document.createElement("a");
            hiddenElement.href = "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURI(csv);
            hiddenElement.target = "_blank";
            hiddenElement.download = "streamlined_sample_customer_import.csv";
            hiddenElement.click();
        }
    };

    return (
        <Box width="500px">
            <Form onSubmit={handleCSVUpload} onChange={handleCSVUploadChange}>
                <DisplayInlineBlockBox margin={{ vertical: "xsmall" }} wrap>
                    Upload your CSV here. Remember to name your columns according to our{" "}
                    <Anchor onClick={loadSampleCSV}>
                        <DisplayInlineBlockBox>
                            <Box align="center" gap="xxsmall" direction="row">
                                <u>sample csv file</u>
                                <FontAwesomeIcon SelectedIcon={IconDownload} size="xxsmall"/>
                            </Box>
                        </DisplayInlineBlockBox>
                    </Anchor>.
                </DisplayInlineBlockBox>
                <FormField
                    component={FileInput}
                    maxSize={10485760}
                    multiple={false}
                    name="csv"
                    renderFile={(file) => (
                        <Box direction="row" gap="xsmall">
                            <FontAwesomeIcon SelectedIcon={IconFile} />
                            {file.name}
                        </Box>
                    )}
                />
                {headers.length > 0 && (
                    <StatusMessage
                        heading={`We detected ${headers.length} columns:`}
                        message={`${headers
                            .filter(Boolean)
                            .join(", ")
                            .replace(/, ([^,]*)$/, ", and $1")}`}
                        type="info"
                        fontSize="small"
                        headingSize="medium"
                        condensed
                    />
                )}
                {error && headers.length === 0 && (
                    <StatusMessage
                        heading={"Uh oh! We cannot recognize any column names"}
                        message={"Please check your file and try again."}
                        fontSize="small"
                        type="error"
                        condensed
                        headingSize="medium"
                    />
                )}
                <Box as="footer" direction="row" align="center" justify="end" margin={{top: 'small'}}>
                    <Button
                        event={{section: 'upload_csv_form', element: 'upload'}}  
                        type="submit"
                        label="Upload"
                        {...(csvFile && !error ? { primary: true } : { secondary: true })}
                        disabled={error || headers.length === 0 || !csvFile}
                    />
                </Box>
            </Form>
        </Box>
    );
};

export default UploadCSVForm;
